import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface KaycooKeyCardProps {
  clientId: string | null;
  /** Current value of clients.settings, including kaycoo_api_key. */
  settings: { kaycoo_api_key?: string } & Record<string, unknown> | null;
  isLoading: boolean;
  onSettingsUpdated: (settings: Record<string, unknown>) => void;
  className?: string;
}

/** Mask all but the prefix and last 4 chars of an API key for display. */
function maskKey(key: string): string {
  if (!key) return '';
  if (key.length <= 12) return '•'.repeat(key.length);
  const prefix = key.slice(0, 4);
  const suffix = key.slice(-4);
  return `${prefix}${'•'.repeat(Math.max(8, key.length - 8))}${suffix}`;
}

export function KaycooKeyCard({
  clientId,
  settings,
  isLoading,
  onSettingsUpdated,
  className,
}: KaycooKeyCardProps) {
  const [draft, setDraft] = useState('');
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);

  const stored = settings?.kaycoo_api_key ?? '';

  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const handleSave = async () => {
    if (!clientId) return;
    const trimmed = draft.trim();
    if (trimmed && !trimmed.startsWith('kap_')) {
      toast.error('Kaycoo API keys start with kap_. Double-check the value before saving.');
      return;
    }
    setSaving(true);
    const nextSettings = { ...(settings ?? {}), kaycoo_api_key: trimmed || undefined };
    const { error } = await supabase
      .from('clients')
      .update({ settings: nextSettings })
      .eq('id', clientId);
    setSaving(false);
    if (error) {
      toast.error('Failed to save Kaycoo API key.');
      return;
    }
    toast.success(trimmed ? 'Kaycoo API key saved.' : 'Kaycoo API key cleared.');
    onSettingsUpdated(nextSettings);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-9 w-full" />
        </CardContent>
      </Card>
    );
  }

  const hasChanges = draft.trim() !== stored;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Kaycoo API Key</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Your tenant&rsquo;s Kaycoo application key (<code>kap_...</code>). CMP forwards this on every
          AI call and uses it to read your agent list. Keys are not stored on the Kaycoo platform &mdash;
          you manage them in your Kaycoo dashboard.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {!stored && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No key configured</AlertTitle>
            <AlertDescription>
              Without a Kaycoo API key, the AI-powered agents (Compliance, embeddings, baseline)
              cannot run. Paste your <code>kap_...</code> key from the Kaycoo dashboard to enable them.
            </AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="kaycoo-api-key">API Key</Label>
          <div className="flex gap-2">
            <Input
              id="kaycoo-api-key"
              type={reveal ? 'text' : 'password'}
              value={reveal ? draft : (draft ? maskKey(draft) : '')}
              onChange={(e) => { setDraft(e.target.value); }}
              onFocus={() => { setReveal(true); }}
              placeholder="kap_..."
              autoComplete="off"
              spellCheck={false}
              className="font-mono text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              type="button"
              onClick={() => { setReveal((r) => !r); }}
              aria-label={reveal ? 'Hide key' : 'Reveal key'}
            >
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Stored in <code>clients.settings.kaycoo_api_key</code>. Only tenant admins can read it
            (RLS-enforced).
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() => { void handleSave(); }}
            disabled={!clientId || saving || !hasChanges}
            size="sm"
          >
            {saving ? 'Saving…' : draft.trim() ? 'Save Key' : 'Clear Key'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
